import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  CreditCard,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Zap,
  RefreshCw,
  Info
} from 'lucide-react';
import { useExpressAccount, usePlatformFees, usePayrollPayments } from '@/hooks/connect';
import { BusinessDetails } from '@/types/index';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';

interface PaymentProcessingSettingsProps {
  businessDetails: BusinessDetails;
  onUpdateSuccess: (updatedDetails: Partial<BusinessDetails>) => void;
}

export const PaymentProcessingSettings: React.FC<PaymentProcessingSettingsProps> = ({
  businessDetails,
  onUpdateSuccess
}) => {
  // Initialize from props - no need to fetch separately
  const [paymentProcessingEnabled, setPaymentProcessingEnabled] = useState(
    businessDetails.payment_processing_enabled ?? false
  );
  const [loading, setLoading] = useState(false);

  // Stripe Connect hooks
  const expressAccount = useExpressAccount(businessDetails.id);
  const platformFees = usePlatformFees(businessDetails.id);
  const payrollPayments = usePayrollPayments(businessDetails.id);

  // Sync state when businessDetails changes (e.g., switching businesses)
  useEffect(() => {
    setPaymentProcessingEnabled(businessDetails.payment_processing_enabled ?? false);
  }, [businessDetails.id, businessDetails.payment_processing_enabled]);

  const togglePaymentProcessing = async (enabled: boolean) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({ payment_processing_enabled: enabled })
        .eq('id', businessDetails.id);

      if (error) throw error;

      setPaymentProcessingEnabled(enabled);
      onUpdateSuccess({ payment_processing_enabled: enabled });
      
      toast.success(
        enabled ? 'Payment processing enabled' : 'Payment processing disabled'
      );
    } catch (error) {
      console.error('Error updating payment processing:', error);
      toast.error('Failed to update payment processing settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!businessDetails.id) return;
    
    try {
      await expressAccount.createAccount(businessDetails.id, businessDetails.country_code);
    } catch (error) {
      console.error('Error creating Express account:', error);
    }
  };

  const handleGenerateOnboardingLink = async () => {
    if (!businessDetails.id) return;
    
    try {
      const url = await expressAccount.generateOnboardingLink(businessDetails.id);
      if (url) {
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error generating onboarding link:', error);
    }
  };

  const getAccountStatusColor = () => {
    if (!expressAccount.accountStatus?.exists) return 'secondary';
    if (expressAccount.isReady) return 'default';
    if (expressAccount.requiresAction) return 'destructive';
    return 'secondary';
  };

  const getAccountStatusText = () => {
    if (!expressAccount.accountStatus?.exists) return 'Not created';
    if (expressAccount.isReady) return 'Active';
    if (expressAccount.requiresAction) return 'Action required';
    if (expressAccount.accountStatus?.onboarding_completed) return 'Onboarding complete';
    return 'Setup in progress';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Payment Processing
        </CardTitle>
        <CardDescription>
          Configure automated payroll payment processing with Stripe Connect
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Payment Processing Toggle */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="text-sm font-medium">Enable Payment Processing</h3>
            <p className="text-sm text-muted-foreground">
              Allow automated payroll payments to employees via bank transfers
            </p>
          </div>
          <Switch
            checked={paymentProcessingEnabled}
            onCheckedChange={togglePaymentProcessing}
            disabled={loading}
          />
        </div>

        <Separator />

        {/* Express Account Status */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium">Stripe Connect Account</h3>
              <p className="text-sm text-muted-foreground">
                Required for processing payments
              </p>
            </div>
            <Badge variant={getAccountStatusColor()}>
              {getAccountStatusText()}
            </Badge>
          </div>

          {/* Account Details */}
          {expressAccount.accountStatus && (
            <div className="grid gap-3 p-4 bg-muted/50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium">Charges Enabled</span>
                  <div className="flex items-center gap-2 mt-1">
                    {expressAccount.accountStatus.charges_enabled ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-orange-600" />
                    )}
                    <span className="text-sm">
                      {expressAccount.accountStatus.charges_enabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-sm font-medium">Payouts Enabled</span>
                  <div className="flex items-center gap-2 mt-1">
                    {expressAccount.accountStatus.payouts_enabled ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-orange-600" />
                    )}
                    <span className="text-sm">
                      {expressAccount.accountStatus.payouts_enabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
              
              {expressAccount.accountStatus.country && (
                <div>
                  <span className="text-sm font-medium">Country</span>
                  <p className="text-sm mt-1">{expressAccount.accountStatus.country}</p>
                </div>
              )}
            </div>
          )}

          {/* Account Actions */}
          <div className="flex gap-2">
            {!expressAccount.accountStatus?.exists ? (
              <Button
                onClick={handleCreateAccount}
                disabled={expressAccount.creating}
                className="gap-2"
              >
                <Zap className="h-4 w-4" />
                {expressAccount.creating ? 'Creating...' : 'Create Account'}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleGenerateOnboardingLink}
                  disabled={expressAccount.loading}
                  className="gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  {expressAccount.isReady ? 'Manage Account' : 'Complete Setup'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => expressAccount.refreshStatus(businessDetails.id)}
                  disabled={expressAccount.loading}
                  className="gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${expressAccount.loading ? 'animate-spin' : ''}`} />
                  Refresh Status
                </Button>
              </>
            )}
          </div>

          {/* Requirements Alert */}
          {expressAccount.requiresAction && expressAccount.accountStatus?.requirements && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-medium">Account requires attention:</p>
                  {expressAccount.accountStatus.requirements.currently_due?.length > 0 && (
                    <ul className="list-disc list-inside text-sm">
                      {expressAccount.accountStatus.requirements.currently_due.map((req, index) => (
                        <li key={index}>{req.replace('_', ' ')}</li>
                      ))}
                    </ul>
                  )}
                  <Button
                    size="sm"
                    onClick={handleGenerateOnboardingLink}
                    className="mt-2"
                  >
                    Resolve Issues
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <Separator />

        {/* Platform Fees */}
        {platformFees.feeStructure && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium">Platform Fees</h3>
              <p className="text-sm text-muted-foreground">
                Fees charged for payment processing services
              </p>
            </div>

            <div className="grid gap-3 p-4 bg-muted/50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium">Fee Structure</span>
                  <p className="text-sm mt-1 capitalize">{platformFees.feeStructure.fee_type}</p>
                </div>
                <div>
                  <span className="text-sm font-medium">Rate</span>
                  <p className="text-sm mt-1">
                    {platformFees.feeStructure.fee_percentage ? 
                      `${(platformFees.feeStructure.fee_percentage * 100).toFixed(2)}%` : 
                      'Custom'
                    }
                    {platformFees.feeStructure.min_fee_cents && 
                      ` + €${(platformFees.feeStructure.min_fee_cents / 100).toFixed(2)} min`
                    }
                  </p>
                </div>
              </div>
              
              {platformFees.hasCustomStructure && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    You have a custom fee structure. Contact support for modifications.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        )}

        {/* Recent Payment Activity */}
        {payrollPayments.summary && payrollPayments.summary.total_payments > 0 && (
          <>
            <Separator />
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium">Recent Activity</h3>
                <p className="text-sm text-muted-foreground">
                  Summary of recent payroll payments
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-lg font-bold">{payrollPayments.summary.total_payments}</div>
                  <div className="text-sm text-muted-foreground">Total Payments</div>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-lg font-bold text-green-600">
                    {(payrollPayments.summary.success_rate * 100).toFixed(1)}%
                  </div>
                  <div className="text-sm text-muted-foreground">Success Rate</div>
                </div>
              </div>

              {payrollPayments.summary.failed_payments > 0 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {payrollPayments.summary.failed_payments} payment{payrollPayments.summary.failed_payments > 1 ? 's' : ''} failed recently. 
                    Check the Payroll → Payments tab for details.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </>
        )}

        {/* Info Section */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-medium">About Payment Processing:</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Requires a verified Stripe Connect Express account</li>
                <li>Supports SEPA and domestic bank transfers</li>
                <li>Payments typically arrive within 1-3 business days</li>
                <li>Platform fees apply to each processed payment</li>
                <li>Full transaction history and reconciliation available</li>
              </ul>
            </div>
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};